/**
 * Client-facing report chrome (header + footer).
 *
 * Matches hireiq-report-mock.html / design mock. Render only on the public
 * `/client/:token/report` route — not staff preview or the demo baseline.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { fmtDateIST } from '@/lib/format';
import { HireIqMark } from '@/components/HireIqLogo';

/** UI label for the magazine report layout version shown to clients. */
export const CLIENT_REPORT_UI_VERSION = '3.2.1';

export function ClientReportHeader({ issuedAt }) {
  const generatedLabel = issuedAt
    ? fmtDateIST(issuedAt, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <header
      className="border-b border-[hsl(var(--border))] bg-card"
      data-testid="client-report-shell-header"
    >
      <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-x-[22px] gap-y-2 px-6 py-3">
        <div className="flex shrink-0 items-center gap-2.5">
          <HireIqMark size="md" className="h-[38px] w-[38px]" data-testid="client-report-brand-mark" />
          <div className="min-w-0">
            <div className="whitespace-nowrap text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
              Kadel Labs {'\u00b7'} HireIQ
            </div>
            <div className="mt-px whitespace-nowrap text-[16.5px] font-bold tracking-tight text-foreground">
              Candidate Evaluation Report
            </div>
          </div>
        </div>

        <div
          className="hidden h-auto min-h-[38px] w-px self-stretch bg-[hsl(var(--border))] xl:block"
          aria-hidden
        />

        <p className="min-w-0 flex-1 basis-full text-[16px] leading-snug text-foreground/70 xl:basis-auto xl:overflow-hidden xl:text-ellipsis xl:whitespace-nowrap">
          <strong className="font-bold text-foreground">One link, every signal.</strong>
          {' '}
          Everything you need to decide, faster and fairer {'\u2014'} in a single view.
        </p>

        <div className="flex shrink-0 gap-1.5">
          <span className="whitespace-nowrap rounded-full border border-[hsl(var(--border))] bg-secondary px-2.5 py-1 text-[10.5px] font-semibold text-muted-foreground">
            Report v{CLIENT_REPORT_UI_VERSION}
          </span>
          {generatedLabel && (
            <span className="whitespace-nowrap rounded-full border border-[hsl(var(--border))] bg-secondary px-2.5 py-1 text-[10.5px] font-semibold text-muted-foreground">
              Generated {generatedLabel}
            </span>
          )}
        </div>
      </div>
    </header>
  );
}

export function ClientReportFooter() {
  const year = new Date().getFullYear();

  return (
    <footer
      className="mt-2 bg-[#0f1729] text-[#c9d2e3]"
      data-testid="client-report-shell-footer"
    >
      <div className="mx-auto max-w-[1440px] px-6 pb-7 pt-9">
        <div className="mb-[22px] flex gap-2.5 rounded-xl border border-[rgba(220,38,38,0.35)] bg-[rgba(220,38,38,0.12)] px-4 py-3.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#fca5a5]" aria-hidden />
          <div className="min-w-0">
            <div className="mb-0.5 text-[12.5px] font-bold text-[#fca5a5]">
              Not the intended recipient?
            </div>
            <p className="m-0 text-[12.5px] leading-relaxed text-[#e2b8b8]">
              This report was generated for a specific, verified email address only. If you received
              this link in error, please close this window now, do not forward, copy, screenshot, or
              store any part of this report, and notify{' '}
              <a
                href="mailto:careers@kadellabs.com"
                className="font-semibold text-[#fca5a5] underline-offset-2 hover:underline"
              >
                careers@kadellabs.com
              </a>
              {' '}
              so we can revoke access.
            </p>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-7 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <h5 className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#7d8bab]">
              Confidentiality
            </h5>
            <p className="mb-2 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              This report and all linked media (video responses, transcripts, assessment scores, and
              panel notes) are confidential and intended solely for authorized hiring personnel at
              Kadel Labs involved in this requisition.
            </p>
            <p className="m-0 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              Every access to this link is logged, including viewer email, IP, and timestamp, for
              audit purposes.
            </p>
          </div>
          <div>
            <h5 className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#7d8bab]">
              Candidate Data &amp; Ownership
            </h5>
            <p className="mb-2 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              The information in this report belongs to the candidate and is processed strictly for
              evaluation of this application. It must not be copied, exported, or reused for any
              purpose outside this hiring decision.
            </p>
            <p className="m-0 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              Retention and deletion follow Kadel Labs&apos; candidate data policy and applicable
              data protection law.
            </p>
          </div>
          <div>
            <h5 className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#7d8bab]">
              Safe Harbor
            </h5>
            <p className="mb-2 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              Scores, summaries, and recommendations in this report are generated with AI assistance
              and reflect a point-in-time assessment. They are advisory inputs to a human hiring
              decision, not a guarantee of candidate performance or fitness.
            </p>
            <p className="m-0 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              Kadel Labs disclaims liability for decisions made solely on the basis of this report
              without independent human review.
            </p>
          </div>
          <div>
            <h5 className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#7d8bab]">
              Contact
            </h5>
            <p className="mb-2 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              Talent Acquisition:
              <br />
              <a
                href="mailto:careers@kadellabs.com"
                className="font-semibold text-[#8fc2ff] hover:underline"
              >
                careers@kadellabs.com
              </a>
            </p>
            <p className="m-0 text-[12.5px] leading-relaxed text-[#aeb8cf]">
              Data privacy / report access issues:
              <br />
              <a
                href="mailto:careers@kadellabs.com"
                className="font-semibold text-[#8fc2ff] hover:underline"
              >
                careers@kadellabs.com
              </a>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-[18px] text-[12px] text-[#7d8bab]">
          <div>
            {'\u00a9'} {year} Kadel Labs {'\u00b7'} HireIQ Candidate Evaluation Report {'\u00b7'}{' '}
            Maintain strict secrecy {'\u2014'} do not forward
          </div>
          <div className="flex flex-wrap gap-4">
            <a href="#privacy" className="text-[#aeb8cf] hover:underline">Privacy Policy</a>
            <a href="#data-handling" className="text-[#aeb8cf] hover:underline">Data Handling</a>
            <a
              href="mailto:careers@kadellabs.com?subject=HireIQ%20report%20issue"
              className="text-[#aeb8cf] hover:underline"
            >
              Report an Issue
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
